<template>
  <PanelLayout
    id="export-details-panel"
    :heading="$t('pages.export.view.heading')"
    :info="item ? (item.typeLabel || item.type) : null"
    :loading="loading"
    :show-footer="false"
  >
    <template #actions-right>
      <OButton icon="refresh" flat :disabled="refreshing" @click="fetchExport()">
        {{ $t('pages.export.view.refresh') }}
      </OButton>
    </template>

    <OScroll class="h-full">
      <div class="p-4 md:p-5 space-y-5">
        <OAlert v-if="loadError" show variant="danger">
          {{ $t('pages.export.view.loadError') }}
        </OAlert>

        <template v-if="item">
          <dl class="divide-y dark:divide-gray-800">
            <ODescriptionDetails :term="$t('general.status')" term-col="sm:col-span-2" definition-col="sm:col-span-4">
              <ExportStatus :item="item" />
            </ODescriptionDetails>
            <ODescriptionDetails
              v-for="detail in details"
              :key="detail.label"
              :term="detail.label"
              term-col="sm:col-span-2"
              definition-col="sm:col-span-4"
              definition-class="mt-1 text-sm leading-5 sm:mt-0 break-words whitespace-pre-line"
            >
              {{ detail.value }}
            </ODescriptionDetails>
          </dl>

          <OAlert v-if="resultMessage" show :variant="item.isError || item.isInvalid ? 'danger' : 'info'">
            <p class="whitespace-pre-line break-words">{{ resultMessage }}</p>
          </OAlert>

          <section v-if="item.isParentExport && item.children?.length" class="space-y-3">
            <h3 class="text-sm font-semibold">{{ $t('pages.export.view.files') }}</h3>
            <div
              v-for="(child, index) in item.children"
              :key="child.idExport"
              class="flex flex-wrap items-center justify-between gap-3 border rounded-lg p-3"
            >
              <div class="space-y-2 text-sm">
                <p>{{ $t('components.export.actions.download.part', { count: index + 1 }) }}</p>
                <ExportStatus :item="child" />
              </div>
              <OButton v-if="canDownload(child)" type="a" :href="child.link ?? undefined" :download="child.type" target="_blank" icon="save" flat>
                {{ $t('general.download') }}
              </OButton>
            </div>
          </section>
          <OButton v-else-if="canDownload(item)" type="a" :href="item.link ?? undefined" :download="item.type" target="_blank" icon="save">
            {{ $t('general.download') }}
          </OButton>
        </template>
      </div>
    </OScroll>
  </PanelLayout>
</template>

<script setup lang="ts">
import PanelLayout from '@/components/panel/PanelLayout.vue'
import ExportStatus from '@/components/export/Status.vue'
import { OAlert, OButton, ODescriptionDetails, OScroll } from '@/components/elements'
import type { ExportModel } from '@/_api/models/export'

const props = defineProps<{ id: string }>()
const { $api, $date } = useNuxtApp()
const { t } = useI18n()

const item = ref<ExportModel | null>(null)
const loading = ref(true)
const refreshing = ref(false)
const loadError = ref(false)

const formatDate = (value?: number) => value
  ? $date.format($date.local(new Date(value * 1000)), $date.presets.full)
  : '—'

const details = computed(() => {
  if (!item.value) return []
  return [
    { label: t('general.type'), value: item.value.typeLabel || item.value.type },
    { label: t('general.info'), value: item.value.detailsLabel || '—' },
    { label: t('pages.export.view.createdBy'), value: item.value.employee?.displayName || t('pages.export.view.notRecorded') },
    { label: t('general.created'), value: formatDate(item.value.created) },
    { label: t('general.modified'), value: formatDate(item.value.modified) },
    { label: t('general.expiry'), value: formatDate(item.value.expiry) },
    { label: t('pages.export.view.recordCount'), value: item.value.result?.count ?? '—' },
  ]
})

const resultMessage = computed(() => {
  const result = item.value?.result
  // Failed jobs may return an array of messages instead of a result object.
  return Array.isArray(result)
    ? result.flat().filter((message): message is string => typeof message === 'string').join('\n')
    : result?.message
})

const canDownload = (exportItem: ExportModel) => exportItem.isComplete
  && !exportItem.isExpired && !exportItem.isInvalid && !exportItem.isParentExport && !!exportItem.link

let fetchExport: () => Promise<void> = async () => {}

watch(() => props.id, (id, _previousId, onCleanup) => {
  let disposed = false
  let timer: ReturnType<typeof setTimeout> | undefined
  item.value = null
  loading.value = true
  refreshing.value = false

  fetchExport = async () => {
    if (disposed || refreshing.value) return
    clearTimeout(timer)
    refreshing.value = true
    loadError.value = false
    try {
      const response = await $api.export.get(id)
      if (disposed) return
      item.value = response.data ?? null
      loadError.value = !item.value
      if (item.value?.isQueued || item.value?.isRunning) {
        timer = setTimeout(fetchExport, 5000)
      }
    } catch {
      if (!disposed) loadError.value = true
    } finally {
      if (!disposed) {
        loading.value = false
        refreshing.value = false
      }
    }
  }

  onCleanup(() => {
    disposed = true
    clearTimeout(timer)
  })
  void fetchExport()
}, { immediate: true })
</script>
